import { useCallback, useMemo } from 'react'
import * as dateFNS from 'date-fns'
import { isDate } from '~/utils'
import useDateLocale from './useDateLocale'

const dateISOFormat = 'yyyy-MM-dd'
const timeISOFormat = 'HH:mm:ss'

const useDateTimeFormat = (props = {}) => {
  const { dfns, locale } = useDateLocale({ locale: props.locale })

  const dateFormat = useMemo(() => {
    const parts = []
    if (!props.hideDate) {
      parts.push(
        locale.formatLong.date({
          width: props.dateFormat ?? 'short',
        })
      )
    }

    if (!props.hideTime) {
      parts.push(
        locale.formatLong
          .time({
            width: props.timeFormat ?? 'short',
          })
          .replace(/^h([^h]+)/, 'hh$1')
      )
    }

    return parts.join(' ')
  }, [
    locale,
    props.dateFormat,
    props.hideDate,
    props.hideTime,
    props.timeFormat,
  ])

  const formatDateTime = useCallback(
    (date, format = dateFormat, options = {}) => {
      return isDate(date)
        ? dateFNS.format(date, format, { locale, ...options })
        : undefined
    },
    [dateFormat, locale]
  )

  const formatDistanceToNow = useCallback(
    (date, options = {}) => {
      return dateFNS.formatDistanceToNow(date, { locale, ...options })
    },
    [locale]
  )

  const getWeekOfMonth = useCallback(
    (date, options = {}) => {
      return dateFNS.getWeekOfMonth(date, { locale, ...options })
    },
    [locale]
  )

  const formatISODate = useCallback(
    (date) => formatDateTime(date, dateISOFormat),
    [formatDateTime]
  )

  const formatISOTime = useCallback(
    (date) => formatDateTime(date, timeISOFormat),
    [formatDateTime]
  )

  const isFriday = useCallback((date) => dateFNS.isFriday(date), [])
  const isWeekend = useCallback((date) => dateFNS.isWeekend(date), [])

  const months = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale.code, { month: 'short' })
    const months = []
    for (let i = 0; i < 12; i++) {
      months.push({
        name: format.format(new Date(0, i)),
      })
    }
    return months
  }, [locale.code])

  const parseDateTime = useCallback(
    (str, format = dateFormat) => {
      return dateFNS.parse(str, format, new Date())
    },
    [dateFormat]
  )

  return useMemo(
    () => ({
      ...dateFNS,
      dateFormat,
      dfns,
      formatDateTime,
      formatDistanceToNow,
      formatISODate,
      formatISOTime,
      getWeekOfMonth,
      isFriday,
      isWeekend,
      months,
      parseDateTime,
    }),
    [
      dateFormat,
      dfns,
      formatDateTime,
      formatDistanceToNow,
      formatISODate,
      formatISOTime,
      getWeekOfMonth,
      isFriday,
      isWeekend,
      months,
      parseDateTime,
    ]
  )
}

export default useDateTimeFormat
